import React, { useState, useEffect } from "react";
import { Link as ScrollLink, animateScroll as scroll } from "react-scroll";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/system";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LogoFile from "../assets/site-images/logo.webp";

// Styled Components
const StyledAppBar = styled(AppBar)({
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.04)",
  borderBottom: "1px solid rgba(1, 52, 95, 0.05)",
  position: "fixed",
  zIndex: 1000,
  padding: "5px 20px",
});

const LogoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
});

const LogoImg = styled("img")({
  height: "35px",
  width: "auto",
  "@media (max-width: 600px)": {
    height: "30px",
  },
});

const HighlightedChar = styled("span")({
  color: "var(--Primary-Color, #01345F)",
  fontWeight: "bold",
});

const NavLinks = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "12px",
  alignItems: "center",
  padding: "8px 16px",
  borderRadius: "25px",
  border: "1px solid rgba(0, 0, 0, 0.2)",
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.05)",
  marginRight: "5rem",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const StyledScrollLink = styled(ScrollLink)(({ active }) => ({
  cursor: "pointer",
  fontSize: "0.9rem",
  fontWeight: "600",
  textDecoration: "none",
  padding: "10px 20px",
  borderRadius: "20px",
  transition: "background-color 0.3s ease-in-out, color 0.3s ease-in-out",
  color: active ? "#ffffff" : "var(--Primary-Color, #01345F)",
  backgroundColor: active ? "var(--Primary-Color, #01345F)" : "transparent",
  backdropFilter: active ? "blur(8px)" : "none",
  "&:hover": {
    backgroundColor: active ? "var(--Primary-Color, #01345F)" : "rgba(1, 52, 95, 0.08)",
    color: active ? "#ffffff" : "var(--Primary-Color, #01345F)",
    backdropFilter: "blur(8px)",
  },
}));

const StyledRouterLink = styled(RouterLink)(({ active }) => ({
  cursor: "pointer",
  fontSize: "0.9rem",
  fontWeight: "600",
  textDecoration: "none",
  padding: "10px 20px",
  borderRadius: "20px",
  transition: "background-color 0.3s ease-in-out, color 0.3s ease-in-out",
  color: active ? "#ffffff" : "var(--Primary-Color, #01345F)",
  backgroundColor: active ? "var(--Primary-Color, #01345F)" : "transparent",
  backdropFilter: active ? "blur(8px)" : "none",
  "&:hover": {
    backgroundColor: active ? "var(--Primary-Color, #01345F)" : "rgba(1, 52, 95, 0.08)",
    color: active ? "#ffffff" : "var(--Primary-Color, #01345F)",
    backdropFilter: "blur(8px)",
  },
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  color: "#333",
  display: "none",
  [theme.breakpoints.down("md")]: {
    display: "block",
  },
}));
const sections = ["home", "how-it-works", "key-benefits", "contact-us"];

const Header = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [activeLink, setActiveLink] = useState("home");
  const [mobileOpen, setMobileOpen] = useState(false);

  const isHomePage = location.pathname === "/";

  // Toggle Mobile Menu
  const toggleDrawer = (open) => () => {
    setMobileOpen(open);
  };

  // Function to determine which section is in view
  const handleScroll = React.useCallback(() => {
    if (!isHomePage) return;
    
    const scrollPosition = window.scrollY;
    let activeSection = "home"; // Default section

    sections.forEach((section) => {

      const sectionElement = document.getElementById(section);
      if (sectionElement) {
        const { offsetTop, offsetHeight } = sectionElement;
        if (
          scrollPosition >= offsetTop - 100 &&
          scrollPosition < offsetTop + offsetHeight - 100
        ) {
          activeSection = section;
        }
      }
    });

    setActiveLink(activeSection);
  }, [isHomePage]);

  useEffect(() => {
    if (isHomePage) {
      handleScroll();
      window.addEventListener("scroll", handleScroll);
    } else {
      setActiveLink(location.pathname.replace("/", ""));
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isHomePage, location.pathname, handleScroll]);


  const handleLogoClick = () => {
    if (isHomePage) {
      scroll.scrollToTop();
    } else {
      navigate("/");
    }
  };

  return (
    <StyledAppBar position="fixed">
      <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
        {/* Logo */}
        <LogoContainer onClick={handleLogoClick}>
          <LogoImg src={LogoFile} alt="RigZip Logo" />
        </LogoContainer>

        {/* Navigation Links (Desktop) */}
        {!isMobile && !isTablet && (
          <NavLinks>
            {sections.map((section) => (
              isHomePage ? (
                <StyledScrollLink
                  key={section}
                  to={section}
                  smooth={true}
                  duration={500}
                  spy={true}
                  offset={-70}
                  active={activeLink === section ? 1 : 0}
                  onSetActive={() => setActiveLink(section)}
                >
                  {section.replace("-", " ").toUpperCase()}
                </StyledScrollLink>
              ) : (
                <StyledRouterLink
                  key={section}
                  to={section === "contact-us" ? "/contact-us" : `/#${section}`}
                  active={activeLink === section ? 1 : 0}
                  onClick={() => {
                    if (section !== "contact-us") navigate(`/`);
                  }}
                >
                  {section.replace("-", " ").toUpperCase()}
                </StyledRouterLink>
              )
            ))}
          </NavLinks>
        )}


        {/* Mobile Menu Button */}
        {(isMobile || isTablet) && (
          <MenuButton onClick={toggleDrawer(true)}>
            <MenuIcon />
          </MenuButton>
        )}

        {/* Mobile Drawer (Sidebar) */}
        <Drawer anchor="right" open={mobileOpen} onClose={toggleDrawer(false)}>
          <Box sx={{ width: 250, padding: "20px" }} role="presentation">
            <IconButton
              onClick={toggleDrawer(false)}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
              }}
            >
              <CloseIcon />
            </IconButton>
            <List sx={{ marginTop: "40px" }}>
              {sections.map((section) => (
                <ListItem 
                  button 
                  key={section} 
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemText>
                    {isHomePage ? (
                      <ScrollLink
                        to={section}
                        smooth={true}
                        duration={500}
                        spy={true}
                        offset={-70}
                        onClick={() => {
                          setActiveLink(section);
                          setMobileOpen(false);
                        }}
                        style={{
                          textDecoration: "none",
                          color: activeLink === section ? "var(--Primary-Color, #01345F)" : "#333",
                          fontWeight: "bold",
                          fontSize: "1rem",
                          display: "block",
                          padding: "10px",
                        }}
                      >
                        {section.replace("-", " ").toUpperCase()}
                      </ScrollLink>
                    ) : (
                      <RouterLink
                        to={section === "contact-us" ? "/contact-us" : "/"}
                        style={{
                          textDecoration: "none",
                          color: activeLink === section ? "var(--Primary-Color, #01345F)" : "#333",
                          fontWeight: "bold",
                          fontSize: "1rem",
                          display: "block",
                          padding: "10px",
                        }}
                      >
                        {section.replace("-", " ").toUpperCase()}
                      </RouterLink>
                    )}
                  </ListItemText>
                </ListItem>
              ))}
            </List>

          </Box>
        </Drawer>
      </Toolbar>
    </StyledAppBar>
  );
};

export default Header;

